import { jsToFormData, paginatedTransform, rawTransform } from 'src/services/skuit/utils'
import {
  Attachment,
  ASN,
  ASNExpectedUnit,
  ASNUnit,
  Csv,
  CustomField,
  PaginatedResponse,
} from '../../api/models'

import { API, CustomAxiosConfig } from './types'
import fileDownload from 'js-file-download'

export interface ExportProps {
  fields: string[]
}

class Attachments {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (guid: string, config: CustomAxiosConfig = {}): Promise<PaginatedResponse<Attachment>> => {
    const response = await this.api.get(`/shipping/asn/${guid}/attachments`, config)
    return paginatedTransform(Attachment)(response.data)
  }

  create = async (guid: string, data: any, config: CustomAxiosConfig = {}): Promise<any> => {
    const axiosConfig = {
      ...config,
      headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' },
    }
    return await this.api.post(
      `/shipping/asn/${guid}/attachments`,
      jsToFormData(data),
      axiosConfig
    )
  }
}

class CustomFields {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<CustomField>> => {
    const response = await this.api.get('/shipping/asn/custom-fields', config)
    return paginatedTransform(CustomField)(response.data)
  }

  create = async (data: any = {}, config: CustomAxiosConfig = {}): Promise<CustomField> => {
    const response = await this.api.post('/shipping/asn/custom-fields', data, config)
    return rawTransform(CustomField)(response.data)
  }
}

class CustomFieldValues {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<CustomField>> => {
    const response = await this.api.get('/shipping/asn-custom-field-values', config)
    return paginatedTransform(CustomField)(response.data)
  }

  create = async (data: any = {}, config: CustomAxiosConfig = {}): Promise<CustomField> => {
    const response = await this.api.post('/shipping/asn-custom-field-values', data, config)
    return rawTransform(CustomField)(response.data)
  }
}

class Units {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<ASNUnit>> => {
    const response = await this.api.get('/shipping/asn-units', config)
    return paginatedTransform(ASNUnit)(response.data)
  }

  create = async (data: any = {}, config: CustomAxiosConfig = {}): Promise<ASNUnit> => {
    const response = await this.api.post('/shipping/asn-units', data, config)
    return rawTransform(ASNUnit)(response.data)
  }
}

class ExpectedUnits {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<ASNExpectedUnit>> => {
    const response = await this.api.get('/shipping/asn-expected-units', config)
    return paginatedTransform(ASNExpectedUnit)(response.data)
  }
}

export class ASNAPI {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<ASN>> => {
    const response = await this.api.get('/shipping/asn', config)
    return paginatedTransform(ASN)(response.data)
  }

  create = async (data: any, config: CustomAxiosConfig = {}): Promise<ASN> => {
    const response = await this.api.post('/shipping/asn', data, config)
    return rawTransform(ASN)(response.data)
  }

  patch = async (guid: string, data: any, config: CustomAxiosConfig = {}): Promise<ASN> => {
    const response = await this.api.patch(`/shipping/asn/${guid}`, data, config)
    return rawTransform(ASN)(response.data)
  }

  retrieve = async (guid: string, config: CustomAxiosConfig = {}): Promise<ASN> => {
    const response = await this.api.get(`/shipping/asn/${guid}`, config)
    return rawTransform(ASN)(response.data)
  }

  uploadCsv = async (guid: string, config?: CustomAxiosConfig) => {
    const axiosConfig = {
      ...config,
      method: 'post' as 'post',
      url: `/shipping/clients/${guid}/asn/csv`,
      headers: { 'Content-Type': 'multipart/form-data' },
      data: jsToFormData(config?.data),
      customTransform: rawTransform(Csv),
    }
    return await this.api.axiosInstance(axiosConfig)
  }

  export = async ({ fields }: ExportProps) => {
    const params = { fields: fields.join(',') }
    const response = await this.api.axiosInstance({
      url: 'shipping/asn/export',
      params,
      responseType: 'blob'
    })
    fileDownload(response.data, 'asns.csv')
  }

  receivingStats = async (guid: string, config: CustomAxiosConfig = {}): Promise<any> => {
    const response = await this.api.get(`/shipping/asn/${guid}/receiving-stats`, config)
    return response.data
  }

  get customFields() {
    const api = new CustomFields(this.api)
    Object.defineProperty(this, 'customFields', { value: api })
    return api
  }

  get customFieldValues() {
    const api = new CustomFieldValues(this.api)
    Object.defineProperty(this, 'customFieldValues', { value: api })
    return api
  }

  get attachments() {
    const api = new Attachments(this.api)
    Object.defineProperty(this, 'attachments', { value: api })
    return api
  }

  get expectedUnits() {
    const api = new ExpectedUnits(this.api)
    Object.defineProperty(this, 'expectedUnits', { value: api })
    return api
  }

  get units() {
    const api = new Units(this.api)
    Object.defineProperty(this, 'units', { value: api })
    return api
  }
}
